import useSWR from "swr";
import { ArrowRight, Home, LayoutGrid, MapPin } from "lucide-react";

import api from "../../../../lib/api";
import type {
  CourtComplexRow,
  CourtRoomRow,
  DistrictRow,
  EstablishmentRow,
} from "./types";
import {
  BackButton,
  DataTable,
  DetailHeader,
  MobileInfoRow,
  OrangeIconBox,
  PrimaryButton,
  RowActions,
  StatusBadge,
  rowClassName,
} from "./DistrictCourtUi";
import { formatDisplayDate } from "../../../../utils/Date";

interface CourtComplexesProps {
  district: DistrictRow;
  complexes: CourtComplexRow[];
  isLoading: boolean;
  onBack: () => void;
  onAdd: () => void;
  onOpen: (complex: CourtComplexRow) => void;
  onEdit: (complex: CourtComplexRow) => void;
  onDelete: (complex: CourtComplexRow) => void;
}

type ComplexStats = {
  establishmentCount: number;
  courtRoomCount: number;
};

const getRows = <T,>(response: any): T[] => {
  const payload = response?.data;

  if (payload?.status === false) return [];
  return (payload?.data || payload || []) as T[];
};

const plural = (count: number, label: string) =>
  `${count} ${label}${count === 1 ? "" : "s"}`;


const districtCityName = (district: DistrictRow) =>
  (district.headquarter || district.districtName || "")
    .replace(/^District Court,\s*/i, "")
    .trim();

const locationSummary = (complex: CourtComplexRow) => {
  const address = complex.address || "";
  const parts = address
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length >= 2) {
    const last = parts[parts.length - 1].replace(/\s+\d{5,6}$/, "");
    return `${parts[parts.length - 2]}, ${last}`;
  }

  return address || "N/A";
};

export const CourtComplexes = ({
  district,
  complexes,
  isLoading,
  onBack,
  onAdd,
  onOpen,
  onEdit,
  onDelete,
}: CourtComplexesProps) => {
  const { data: complexStats = {} } = useSWR(
    complexes.length
      ? `new-courts-complex-stats:${complexes
          .map((complex) => `${complex._id}:${complex.courtEstablishment}`)
          .join("|")}`
      : null,
    async () => {
      const entries = await Promise.all(
        complexes.map(async (complex) => {
          if (complex.courtEstablishment) {
            const establishmentsResponse = await api.get(
              `/api/court/get-establishments/${complex._id}`,
            );
            const establishments = getRows<EstablishmentRow>(
              establishmentsResponse,
            );
            const roomResponses = await Promise.all(
              establishments.map((establishment) =>
                api.get(`/api/court/get-courtrooms/${establishment._id}`),
              ),
            );
            const courtRoomCount = roomResponses.reduce(
              (total, response) => total + getRows<CourtRoomRow>(response).length,
              0,
            );

            return [
              complex._id,
              {
                establishmentCount: establishments.length,
                courtRoomCount,
              },
            ] as const;
          }

          const roomsResponse = await api.get(
            `/api/court/get-courtrooms/${complex._id}`,
          );

          return [
            complex._id,
            {
              establishmentCount: 0,
              courtRoomCount: getRows<CourtRoomRow>(roomsResponse).length,
            },
          ] as const;
        }),
      );

      return Object.fromEntries(entries) as Record<string, ComplexStats>;
    },
  );
  const totalCourtRooms = Object.values(complexStats).reduce(
    (total, stats) => total + stats.courtRoomCount,
    0,
  );
  const cityName = districtCityName(district);

  return (
    <div>
      <BackButton label="Back to District Courts" onClick={onBack} />
      <DetailHeader
        icon={<LayoutGrid className="h-6 w-6" />}
        title={district.districtName}
        meta={
          <>
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" />
              {[cityName, district.stateName].filter(Boolean).join(", ")}
            </span>
            <span className="inline-flex items-center gap-1">
              <Home className="h-3.5 w-3.5" />
              {district.headquarter || "N/A"}
            </span>
          </>
        }
        stats={[
          { label: "Complexes", value: complexes.length },
          { label: "Courtrooms", value: totalCourtRooms, dark: true },
        ]}
      />

      <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-slate-500">
          Court complexes under this district court. Click a complex to manage
          its courtrooms.
        </p>
        <PrimaryButton label="Add Court Complex" onClick={onAdd} className="w-full sm:w-auto" />
      </div>

      <DataTable
        headers={[
          "Complex Name",
          "Location / Address",
          "Established",
          "Status",
          "Actions",
        ]}
        isLoading={isLoading}
        emptyLabel="No court complexes yet"
        emptyAction="Add Court Complex"
        onEmptyAction={onAdd}
        mobileCards={complexes.map((complex) => {
          const stats = complexStats[complex._id] || {
            establishmentCount: 0,
            courtRoomCount: 0,
          };
          const details = complex.courtEstablishment
            ? `${plural(stats.establishmentCount, "establishment")} · ${plural(
                stats.courtRoomCount,
                "courtroom",
              )}`
            : plural(stats.courtRoomCount, "courtroom");

          return (
            <article
              key={complex._id}
              className="rounded-xl border border-[#E8EDF5] bg-white p-4 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                  <OrangeIconBox small>
                    <LayoutGrid className="h-5 w-5" />
                  </OrangeIconBox>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => onOpen(complex)}
                        className="text-left font-semibold text-[#f26422]"
                      >
                        {complex.complexName}
                        <ArrowRight className="ml-1 inline h-4 w-4 text-[#f9a07a]" />
                      </button>
                      {complex.courtEstablishment && (
                        <span className="rounded-full border border-[#fcc09a] bg-[#fff4ee] px-2.5 py-0.5 text-xs font-semibold text-[#f26422]">
                          Has establishments
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-400">{details}</div>
                  </div>
                </div>
                <RowActions
                  onEdit={() => onEdit(complex)}
                  onDelete={() => onDelete(complex)}
                />
              </div>
              <div className="mt-4 space-y-3">
                <MobileInfoRow
                  label="Location"
                  value={locationSummary(complex)}
                />
                <MobileInfoRow
                  label="Established"
                  value={formatDisplayDate(complex.established)}
                />
                <MobileInfoRow
                  label="Status"
                  value={<StatusBadge status={complex.status || "Active"} />}
                />
              </div>
            </article>
          );
        })}
      >
        {complexes.map((complex) => {
          const stats = complexStats[complex._id] || {
            establishmentCount: 0,
            courtRoomCount: 0,
          };
          const details = complex.courtEstablishment
            ? `${plural(stats.establishmentCount, "establishment")} · ${plural(
                stats.courtRoomCount,
                "courtroom",
              )}`
            : plural(stats.courtRoomCount, "courtroom");

          return (
            <tr key={complex._id} className={rowClassName}>
              <td className="px-5 py-4">
                <div className="flex items-center gap-3">
                  <OrangeIconBox small>
                    <LayoutGrid className="h-5 w-5" />
                  </OrangeIconBox>
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => onOpen(complex)}
                        className="text-left font-semibold text-[#f26422]"
                      >
                        {complex.complexName}
                        <ArrowRight className="ml-1 inline h-4 w-4 text-[#f9a07a]" />
                      </button>
                      {complex.courtEstablishment && (
                        <span className="rounded-full border border-[#fcc09a] bg-[#fff4ee] px-2.5 py-0.5 text-xs font-semibold text-[#f26422]">
                          Has establishments
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-400">{details}</div>
                  </div>
                </div>
              </td>
              <td className="px-5 py-4 text-sm">
                <div className="font-medium text-slate-800">
                  {locationSummary(complex)}
                </div>
                {complex.address && (
                  <div className="mt-1 text-xs text-slate-400">
                    {complex.address}
                  </div>
                )}
              </td>
              <td className="px-5 py-4 text-sm text-slate-700">
                {formatDisplayDate(complex.established)}
              </td>
              <td className="px-5 py-4">
                <StatusBadge status={complex.status || "Active"} />
              </td>
              <td className="px-5 py-4">
                <RowActions
                  onEdit={() => onEdit(complex)}
                  onDelete={() => onDelete(complex)}
                />
              </td>
            </tr>
          );
        })}
      </DataTable>
    </div>
  );
};
